/**
 * §P.9 · El andamio común de un informe.
 *
 * Los nueve informes comparten exactamente lo mismo: una consulta, un botón de CSV,
 * los cuatro estados de carga y un sobre con `rows` y `totals`. Nueve copias de esas
 * cuarenta líneas es peor que una envoltura, porque el día que el CSV falle de otra
 * manera hay que arreglarlo nueve veces.
 *
 * Lo que **no** entra aquí es la tabla: cada informe se lee distinto y meter una
 * tabla genérica por columnas sería inventar un lenguaje para no escribir seis
 * `<td>`. El seam está donde tiene que estar: los datos los da la envoltura, la
 * forma la pone cada informe.
 *
 * `pedir` es una función y no el nombre de la ruta porque Eden tipa cada llamada por
 * su camino: con una clave dinámica se pierde el tipo y hay que volver al `as`.
 */
import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { opcionesDeSesion } from '@/shared/api/client';
import { descargarFichero } from '@/shared/api/descargas';
import { aProblema, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import type { Informe } from '@/club/informes/tipos';

export function EnvolturaDeInforme<Fila>({
  club,
  clave,
  ruta,
  pedir,
  titulo,
  explicacion,
  vacio,
  children,
}: {
  club: string;
  /** La clave de caché. Distinta por informe, o se pisan entre ellos. */
  clave: string;
  /** El camino, solo para el `?format=csv`. */
  ruta: string;
  pedir: () => Promise<Informe<Fila>>;
  titulo: string;
  explicacion: ReactNode;
  vacio: { titulo: string; explicacion: string };
  children: (datos: Informe<Fila>) => ReactNode;
}) {
  const [problema, setProblema] = useState<Problema | null>(null);
  const [descargando, setDescargando] = useState(false);

  const informe = useQuery({
    queryKey: ['informe', clave, club],
    queryFn: pedir,
  });

  const descargar = async () => {
    setDescargando(true);
    setProblema(null);
    const fallo = await descargarFichero(
      `/v1/clubs/${club}/reports/${ruta}?format=csv`,
      `informe-de-${clave}.csv`,
      opcionesDeSesion
    );
    setDescargando(false);
    if (fallo) setProblema(fallo);
  };

  return (
    <section>
      <header className="titulo-fila">
        <h1>{titulo}</h1>
        {/* El CSV siempre: `BFF_BO.md` §7.6 lo pide en los nueve. */}
        <button
          type="button"
          onClick={() => void descargar()}
          disabled={descargando}
        >
          {descargando ? 'Preparando…' : 'Descargar en CSV'}
        </button>
      </header>

      <p className="tenue">{explicacion}</p>

      {problema && <PanelError problema={problema} />}

      {informe.isPending && (
        <Cargando filas={5} etiqueta={`Calculando ${titulo.toLowerCase()}`} />
      )}

      {informe.isError && (
        <PanelError
          problema={aProblema(informe.error)}
          reintentar={() => void informe.refetch()}
        />
      )}

      {informe.data &&
        (informe.data.rows.length === 0 ? (
          <Vacio titulo={vacio.titulo} explicacion={vacio.explicacion} />
        ) : (
          children(informe.data)
        ))}
    </section>
  );
}
